// Data types enumeration matching the Rust enum
enum DataType {
  NoType = 0,
  U8 = 1,
  I8 = 2,
  U16 = 3,
  I16 = 4,
  U32 = 5,
  I32 = 6,
  U64 = 7,
  I64 = 8,
  F32 = 9,
  F64 = 10,
}

interface GBFHeader {
  version: number;
  fieldCount: number;
  fieldTypes: DataType[];
  fieldNames: string[];
}

interface GBFRecord {
  [key: string]: number;
}

interface PerformanceMetrics {
  totalTime: number;
  headerParseTime: number;
  recordsParseTime: number;
  recordCount: number;
  bytesProcessed: number;
  recordsPerSecond: number;
  megabytesPerSecond: number;
}


export class GBFDecoder {
  private buffer: ArrayBuffer;
  private view: DataView;
  private position: number = 0;
  private metrics: Partial<PerformanceMetrics> = {};

  constructor(buffer: ArrayBuffer) {
    this.buffer = buffer;
    this.view = new DataView(buffer);
  }

  private readBytes(length: number): Uint8Array {
    const bytes = new Uint8Array(this.buffer, this.position, length);
    this.position += length;
    return bytes;
  }

  private readString(length: number): string {
    const bytes = this.readBytes(length);
    return new TextDecoder('ascii').decode(bytes);
  }

  private readNullTerminatedString(): string {
    const start = this.position;
    while (this.position < this.buffer.byteLength && this.view.getUint8(this.position) !== 0) {
      this.position++;
    }
    const length = this.position - start;
    this.position = start;
    const str = this.readString(length);
    this.position++; // Skip null terminator
    return str;
  }

  private readU8(): number {
    const value = this.view.getUint8(this.position);
    this.position += 1;
    return value;
  }

  private readU32LE(): number {
    const value = this.view.getUint32(this.position, true); // little endian
    this.position += 4;
    return value;
  }

  private readF32LE(): number {
    const value = this.view.getFloat32(this.position, true); // little endian
    this.position += 4;
    return value;
  }

  /*private getDataTypeSize(dataType: DataType): number {
    switch (dataType) {
      case DataType.U8:
      case DataType.I8:
        return 1;
      case DataType.U16:
      case DataType.I16:
        return 2;
      case DataType.U32:
      case DataType.I32:
      case DataType.F32:
        return 4;
      case DataType.U64:
      case DataType.I64:
      case DataType.F64:
        return 8;
      default:
        throw new Error(`Unknown data type: ${dataType}`);
    }
  }*/

  private readValueByType(dataType: DataType): number {
    switch (dataType) {
      case DataType.U8:
        return this.view.getUint8(this.position++);
      case DataType.I8:
        return this.view.getInt8(this.position++);
      case DataType.U16:
        const u16 = this.view.getUint16(this.position, true);
        this.position += 2;
        return u16;
      case DataType.I16:
        const i16 = this.view.getInt16(this.position, true);
        this.position += 2;
        return i16;
      case DataType.U32:
        return this.readU32LE();
      case DataType.I32:
        const i32 = this.view.getInt32(this.position, true);
        this.position += 4;
        return i32;
      case DataType.F32:
        return this.readF32LE();
      case DataType.F64:
        const f64 = this.view.getFloat64(this.position, true);
        this.position += 8;
        return f64;
      case DataType.U64:
      case DataType.I64:
        // Note: JavaScript has limited 64-bit integer support
        const low = this.view.getUint32(this.position, true);
        const high = this.view.getUint32(this.position + 4, true);
        this.position += 8;
        return high * 0x100000000 + low;
      default:
        throw new Error(`Unsupported data type: ${dataType}`);
    }
  }

  private parseHeader(): GBFHeader {
    const startTime = performance.now();

    // Check magic bytes: 'eX3Li\0GBF'
    const magic = this.readString(9);
    if (magic !== 'eX3Li\0GBF') {
      throw new Error(`Invalid magic bytes: ${magic}`);
    }

    // Read version
    const version = this.readU8();

    // Read field count (4 bytes, little endian)
    const fieldCount = this.readU32LE();

    // Read field types
    const fieldTypes: DataType[] = [];
    for (let i = 0; i < fieldCount; i++) {
      fieldTypes.push(this.readU8());
    }

    // Check separator '\0E\0'
    const separator = this.readBytes(3);
    const expectedSeparator = new Uint8Array([0, 69, 0]); // '\0E\0'
    if (!separator.every((byte, index) => byte === expectedSeparator[index])) {
      throw new Error('Invalid separator after field types');
    }

    // Read field names (null-terminated strings)
    const fieldNames: string[] = [];
    for (let i = 0; i < fieldCount; i++) {
      fieldNames.push(this.readNullTerminatedString());
    }

    // Check end marker 'oIleXe'
    const endMarker = this.readString(6);
    if (endMarker !== 'oIleXe') {
      throw new Error(`Invalid end marker: ${endMarker}`);
    }

    this.metrics.headerParseTime = performance.now() - startTime;
    return { version, fieldCount, fieldTypes, fieldNames };
  }

  private getTypeSize(dataType: DataType): number {
    switch (dataType) {
      case DataType.U8:
      case DataType.I8:
        return 1;
      case DataType.U16:
      case DataType.I16:
        return 2;
      case DataType.U32:
      case DataType.I32:
      case DataType.F32:
        return 4;
      case DataType.U64:
      case DataType.I64:
      case DataType.F64:
        return 8;
      default:
        throw new Error(`Unsupported data type: ${dataType}`);
    }
  }

  /**
   * Cheap structural check: parses only the header and computes how many whole records the buffer holds,
   * without materializing them. `remainder` != 0 means a truncated trailing record.
   * `firstTime` / `lastTime` are the values of the `time` field of the first / last record (null if absent).
   * Throws if the header is invalid.
   */
  public inspect(): {
    header: GBFHeader;
    dataOffset: number;
    recordSize: number;
    recordCount: number;
    remainder: number;
    firstTime: number | null;
    lastTime: number | null;
  } {
    const header = this.parseHeader();
    const dataOffset = this.position;
    const sizes = header.fieldTypes.map((ft) => this.getTypeSize(ft));
    const recordSize = sizes.reduce((a, b) => a + b, 0);
    const dataBytes = this.buffer.byteLength - dataOffset;
    const recordCount = recordSize > 0 ? Math.floor(dataBytes / recordSize) : 0;
    const remainder = recordSize > 0 ? dataBytes - recordCount * recordSize : dataBytes;

    let firstTime: number | null = null;
    let lastTime: number | null = null;
    const timeIdx = header.fieldNames.indexOf('time');

    if (timeIdx >= 0 && recordCount > 0) {
      const fieldOffset = sizes.slice(0, timeIdx).reduce((a, b) => a + b, 0);
      this.position = dataOffset + fieldOffset;
      firstTime = this.readValueByType(header.fieldTypes[timeIdx]);
      this.position = dataOffset + (recordCount - 1) * recordSize + fieldOffset;
      lastTime = this.readValueByType(header.fieldTypes[timeIdx]);
    }

    this.position = dataOffset;
    return { header, dataOffset, recordSize, recordCount, remainder, firstTime, lastTime };
  }

  public decode(): { header: GBFHeader; records: GBFRecord[]; metrics: PerformanceMetrics } {
    const totalStartTime = performance.now();

    const header = this.parseHeader();
    const records: GBFRecord[] = [];

    // Read records
    const recordsStartTime = performance.now();
    while (this.position < this.buffer.byteLength) {
      const record: GBFRecord = {};
      for (let i = 0; i < header.fieldCount; i++) {
        const fieldName = header.fieldNames[i];
        const fieldType = header.fieldTypes[i];
        record[fieldName] = this.readValueByType(fieldType);
      }

      records.push(record);
    }

    const totalTime = performance.now() - totalStartTime;
    const recordsParseTime = performance.now() - recordsStartTime;

    const metrics: PerformanceMetrics = {
      totalTime,
      headerParseTime: this.metrics.headerParseTime || 0,
      recordsParseTime,
      recordCount: records.length,
      bytesProcessed: this.buffer.byteLength,
      recordsPerSecond: records.length / (totalTime / 1000),
      megabytesPerSecond: (this.buffer.byteLength / (1024 * 1024)) / (totalTime / 1000)
    };


    return { header, records, metrics };
  }
}
